import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Section, Container, Eyebrow, DisplayH1 } from "@/components/Layout";
import { api } from "@/lib/api";

export default function Apply() {
  const [params] = useSearchParams();
  const [programs, setPrograms] = useState([]);
  const nav = useNavigate();
  const [form, setForm] = useState({
    program_slug: params.get("program") || "puppy-academy",
    full_name: "", email: "", phone: "",
    dog_name: "", dog_breed: "", dog_age_months: "",
    goals: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/programs").then((r) => setPrograms(r.data.programs || [])); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...form, dog_age_months: form.dog_age_months ? Number(form.dog_age_months) : null };
      const r = await api.post("/applications", body);
      toast.success("Application received. Our admissions team will be in touch.");
      nav(`/apply/thank-you?id=${r.data.application_id}`);
    } catch (e) {
      toast.error("We couldn't submit your application. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const inputCls = "w-full bg-transparent border-b border-white/20 focus:border-[color:var(--wk-gold)] outline-none py-3 text-lg text-[color:var(--wk-bone)] placeholder:text-[color:var(--wk-muted)]";

  return (
    <Section testid="apply-page" className="pt-40 pb-32">
      <Container>
        <div className="grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <Eyebrow>Admissions · 2026 Season</Eyebrow>
            <DisplayH1 className="mt-6">Apply to the University.</DisplayH1>
            <p className="mt-8 text-lg text-[color:var(--wk-bone-dim)] leading-relaxed">
              Every application is read by an admissions officer within 48 hours.
              We accept a limited number of students each season to preserve the
              standard of our programs.
            </p>
            <div className="mt-10 hairline p-6 rounded-2xl">
              <div className="overline">Concierge Admissions</div>
              <div className="mt-3 font-serif-display text-2xl">+1 (312) 555 · WOLF</div>
              <div className="text-sm text-[color:var(--wk-bone-dim)] mt-1">Monday–Friday · 9am–7pm CT</div>
            </div>
          </div>
          <form onSubmit={submit} data-testid="apply-form" className="lg:col-span-7 space-y-10">
            <div>
              <label className="overline">Program</label>
              <select value={form.program_slug} onChange={set("program_slug")} className={inputCls} data-testid="apply-program">
                {programs.map((p) => <option key={p.slug} value={p.slug} className="bg-[#0a0a0a]">{p.name}</option>)}
              </select>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <label className="overline">Full Name</label>
                <input required value={form.full_name} onChange={set("full_name")} className={inputCls} data-testid="apply-name" />
              </div>
              <div>
                <label className="overline">Email</label>
                <input required type="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="apply-email" />
              </div>
              <div>
                <label className="overline">Phone</label>
                <input value={form.phone} onChange={set("phone")} className={inputCls} data-testid="apply-phone" />
              </div>
              <div>
                <label className="overline">Dog Name</label>
                <input value={form.dog_name} onChange={set("dog_name")} className={inputCls} data-testid="apply-dog-name" />
              </div>
              <div>
                <label className="overline">Dog Breed</label>
                <input value={form.dog_breed} onChange={set("dog_breed")} className={inputCls} data-testid="apply-dog-breed" />
              </div>
              <div>
                <label className="overline">Dog Age (months)</label>
                <input type="number" min="0" value={form.dog_age_months} onChange={set("dog_age_months")} className={inputCls} data-testid="apply-dog-age" />
              </div>
            </div>
            <div>
              <label className="overline">Training Goals</label>
              <textarea required rows={4} value={form.goals} onChange={set("goals")} placeholder="What transformation are you seeking?" className={inputCls + " resize-none"} data-testid="apply-goals" />
            </div>
            <button disabled={busy} type="submit" data-testid="apply-submit" className="btn-gold disabled:opacity-60">
              {busy ? "Submitting…" : "Submit Application"}
            </button>
          </form>
        </div>
      </Container>
    </Section>
  );
}
